import React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { TopicsData } from '../hooks/useTopicsData';
import { SectionCard, SectionHeader, NestedPanel } from './ui/Section';
import { ShareDonut, RankBarChart, formatCompact } from './ui/charts';

interface PlatformBreakdownProps {
  data: TopicsData | null;
  selectedBrands: string[];
  selectedPlatforms: string[];
}

const PlatformBreakdown: React.FC<PlatformBreakdownProps> = ({ 
  data,
  selectedBrands, 
  selectedPlatforms
}) => {
  const theme = useTheme();
  // Platform name mapping (lowercase to proper case)
  const platformNameMapping: Record<string, string> = {
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'linkedin': 'LinkedIn',
    'tiktok': 'TikTok',
    'youtube': 'YouTube',
    'twitter': 'Twitter',
    'x': 'X'
  };

  // Platform colors mapping. TikTok and X are black brands, which would
  // vanish on the dark theme, so they switch to a light ink there.
  const isDark = theme.palette.mode === 'dark';
  const platformColors = {
    'Facebook': '#1877F2',
    'Instagram': '#C13584',
    'LinkedIn': '#0A66C2',
    'TikTok': isDark ? '#25F4EE' : '#1B1526',
    'YouTube': '#FF0000',
    'Twitter': '#1DA1F2',
    'X': isDark ? '#E7E9EA' : '#1B1526'
  };

  // Helper function to get proper platform name
  const getPlatformName = (platform: string): string => {
    return platformNameMapping[platform.toLowerCase()] || platform;
  };

  if (!data) {
    return null;
  }

  // Filter data based on selected brands and platforms
  const filterData = (items: any[]) => {
    let filtered = items;
    
    // Filter by brands if any selected
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(item => selectedBrands.includes(item.Brand));
    }
    
    // Filter by platforms if any selected
    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(item => selectedPlatforms.includes(item.Platform));
    }
    
    return filtered;
  };

  // Aggregate posts by platform from the filtered data
  const filteredPostsData = filterData(data.platform_posts_breakdown);
  const platformPostsMap = filteredPostsData.reduce((acc: { [key: string]: number }, item) => {
    acc[item.Platform] = (acc[item.Platform] || 0) + item.post_count;
    return acc;
  }, {});

  // Aggregate engagement by platform from the filtered data
  const filteredEngagementData = filterData(data.platform_engagement_breakdown);
  const platformEngagementMap = filteredEngagementData.reduce((acc: { [key: string]: { totalEngagement: number; count: number } }, item) => {
    if (!acc[item.Platform]) {
      acc[item.Platform] = { totalEngagement: 0, count: 0 };
    }
    acc[item.Platform].totalEngagement += item.total_engscore;
    acc[item.Platform].count += item.count;
    return acc;
  }, {});

  // Transform platform posts breakdown data for the pie chart
  const postsData = Object.entries(platformPostsMap).map(([platform, count]) => {
    const properName = getPlatformName(platform);
    return {
      name: properName,
      value: count,
      color: platformColors[properName as keyof typeof platformColors] || '#666666'
    };
  });

  // Calculate percentages for share of posts
  const totalPosts = postsData.reduce((sum, item) => sum + item.value, 0);
  const postsDataWithPercentages = postsData.map(item => ({
    ...item,
    value: (item.value / totalPosts) * 100,
    rawValue: item.value // Store the raw count
  })).sort((a, b) => b.value - a.value); // Sort by percentage in descending order

  // Transform platform engagement breakdown data for the pie chart
  const engagementData = Object.entries(platformEngagementMap).map(([platform, data]) => {
    const properName = getPlatformName(platform);
    return {
      name: properName,
      value: data.totalEngagement,
      color: platformColors[properName as keyof typeof platformColors] || '#666666'
    };
  });

  // Calculate percentages for share of engagement
  const totalEngagement = engagementData.reduce((sum, item) => sum + item.value, 0);
  const engagementDataWithPercentages = engagementData.map(item => ({
    ...item,
    value: (item.value / totalEngagement) * 100,
    rawValue: item.value // Store the raw score
  })).sort((a, b) => b.value - a.value); // Sort by percentage in descending order

  // Calculate average engagement per platform
  const averageEngagementData = Object.entries(platformEngagementMap)
    .map(([platform, data]) => {
      const properName = getPlatformName(platform);
      return {
        name: properName,
        value: data.totalEngagement / data.count,
        color: platformColors[properName as keyof typeof platformColors] || '#666666'
      };
    })
    .sort((a, b) => b.value - a.value);

  return (
    <SectionCard>
      <SectionHeader
        title="Platform Breakdown"
        info="Platform Breakdown compares performance across social media platforms. It shows Share of Posts (content volume by platform), Share of Engagement (total engagement distribution), and Average Engagement per Post to identify which platforms drive the most activity and audience interaction."
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2 }}>
        <ShareDonut
          title="Share of Posts"
          data={postsDataWithPercentages}
          rawLabel="Posts"
          centerValue={formatCompact(totalPosts)}
          centerCaption="posts"
        />
        <ShareDonut
          title="Share of Engagement"
          data={engagementDataWithPercentages}
          rawLabel="Engagement"
          centerValue={formatCompact(totalEngagement)}
          centerCaption="engagement"
        />
        <NestedPanel sx={{ gridColumn: { md: '1 / -1', xl: 'auto' } }}>
          <SectionHeader title="Average engagement per post" level="sub" />
          <RankBarChart
            data={averageEngagementData}
            formatValue={(v) => (v >= 1000 ? formatCompact(v) : v === 0 ? '0' : v.toFixed(v < 10 ? 2 : 0))}
            tooltipLabel="Avg. engagement"
            categoryWidth={96}
            rowHeight={40}
          />
        </NestedPanel>
      </Box>
    </SectionCard>
  );
};

export default PlatformBreakdown; 